/**
 * Transport for Ask Addy v2 (the Mastra agent in backend/ask-addy-agent).
 *
 * Enabled when REACT_APP_ASK_ADDY_V2_URL is set; unset, the sidebar renders the current Ask Addy
 * unchanged. Every request carries the loan officer's Firebase ID token (Authorization: Bearer),
 * from the page that embeds the sidebar or the sidebar's URL, so the server takes the user from
 * the token rather than from the identity headers. Prod requires it; dev accepts requests
 * without one until every surface sends it.
 *
 * The chat UI for v2 is built on assistant-ui (src/askAddyV2/); this module only speaks HTTP:
 * it streams v2's server-sent events as parsed objects and loads history.
 */

const BASE_URL = (process.env.REACT_APP_ASK_ADDY_V2_URL || "").replace(/\/$/, "");
const API_KEY = process.env.REACT_APP_ASK_ADDY_V2_KEY || "";

const LOCAL_TIMEZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch (_) {
    return "";
  }
})();

/** Where the sign-in token comes from (set by the sidebar page); none means no token is sent. */
let tokenSource = null;
let cachedToken = { token: "", expiresAt: 0 };
/** Refresh this long before the token expires, so a long answer never starts with a stale one. */
const TOKEN_REFRESH_MARGIN_MS = 5 * 60_000;
/** For a token whose expiry can't be read: reuse it briefly rather than ask the page every request. */
const UNKNOWN_EXPIRY_MS = 10 * 60_000;
/** Bigger files go up in chunks of the size the server names: Cloud Run takes at most 32 MiB a request. */
const SINGLE_UPLOAD_MAX_BYTES = 8 * 1024 * 1024;
/** Tries per chunk when the connection drops or the server hiccups mid-upload. */
const CHUNK_RETRIES = 3;
/** Events that mean the answer is whole: a connection dropped after one of them is not a failure. */
const FINISH_EVENTS = new Set(["final_response", "complete"]);

/** A Firebase ID token's expiry (ms), from its unverified payload; 0 when it can't be read. */
export function tokenExpiry(token) {
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(part.padEnd(part.length + ((4 - (part.length % 4)) % 4), "=")));
    return typeof payload.exp === "number" ? payload.exp * 1000 : 0;
  } catch (_) {
    return 0;
  }
}

async function signInToken(now = Date.now()) {
  if (!tokenSource) return "";
  if (cachedToken.token && cachedToken.expiresAt - TOKEN_REFRESH_MARGIN_MS > now) return cachedToken.token;
  try {
    const token = (await tokenSource()) || "";
    cachedToken = { token, expiresAt: token ? tokenExpiry(token) || now + UNKNOWN_EXPIRY_MS : 0 };
    return token;
  } catch (error) {
    console.warn("[AskAddyV2] no sign-in token", error?.message || error);
    return "";
  }
}

export const requestHeaders = async ({ organizationId, userId }, json = true) => ({
  ...(json ? { "Content-Type": "application/json" } : {}),
  "x-addy-organization-id": organizationId || "",
  "x-addy-user-id": userId || "",
  "x-addy-surface": "sidebar-chat",
  // The LO's computer's time zone: Addy's "today" and the default for jobs created from chat.
  "x-addy-timezone": LOCAL_TIMEZONE,
  ...(API_KEY ? { "x-addy-api-key": API_KEY } : {}),
  ...(await signInToken().then((token) => (token ? { Authorization: `Bearer ${token}` } : {}))),
});

async function failure(response) {
  const text = await response.text().catch(() => "");
  try {
    return JSON.parse(text).reason || text || `HTTP ${response.status}`;
  } catch (_) {
    return text || `HTTP ${response.status}`;
  }
}

/** The default v2 thread for a loan, a Scenario Center scenario, or general questions, as the server derives it. */
export const defaultThreadId = ({ organizationId, userId, loanId, scenarioId }) => {
  if (loanId) return `loan:${organizationId}:${userId}:${loanId}`;
  if (scenarioId) return `scenario:${organizationId}:${userId}:${scenarioId}`;
  return `general:${organizationId}:${userId}`;
};

/** What a request is about: the open loan or, with none open, the open scenario (the server does the same). */
const openRecord = ({ loanId, scenarioId }) => (loanId ? { loanId } : scenarioId ? { scenarioId } : {});

/**
 * A fresh thread for a new chat. Like the server's POST /threads, it extends the default id, so
 * the server can check ownership from the id alone; the thread is stored on its first message.
 */
export const newThreadId = (identity) => `${defaultThreadId(identity)}:${Math.random().toString(36).slice(2, 10)}`;

/**
 * The server writes a heartbeat every 15 s while an answer runs, so this long without a single byte
 * means the connection died without telling us (seen on phones: the answer finished on the server and
 * the sidebar spun forever).
 */
export const STREAM_STALL_MS = 45_000;

/** reader.read(), or a StallError when nothing arrives for `ms`. */
function readWithin(reader, ms) {
  let timer;
  const stalled = new Promise((_, reject) => {
    timer = setTimeout(() => reject(Object.assign(new Error(`no data for ${ms / 1000}s`), { name: "StallError" })), ms);
  });
  return Promise.race([reader.read(), stalled]).finally(() => clearTimeout(timer));
}

export const AskAddyV2 = {
  isEnabled() {
    return Boolean(BASE_URL);
  },

  /** Where to get the loan officer's sign-in token; returns a promise of the token or "". */
  setTokenSource(source) {
    tokenSource = typeof source === "function" ? source : null;
    cachedToken = { token: "", expiresAt: 0 };
  },

  /** v2 download links are relative to its server. */
  absoluteUrl(path) {
    return /^https?:\/\//.test(path || "") ? path : `${BASE_URL}${path || ""}`;
  },

  /**
   * POST and yield each server-sent event as a parsed object ({ type, ... }).
   * Network and HTTP failures are yielded as { type: "error", message } so callers render them
   * instead of throwing into a UI that would show nothing.
   */
  async *events(path, payload, identity, abortSignal) {
    console.info("[AskAddyV2] →", path, { loanId: payload.loanId || "(none)", scenarioId: payload.scenarioId || "(none)", threadId: payload.threadId || "(default)" });
    let response;
    try {
      response = await fetch(`${BASE_URL}${path}`, {
        method: "POST",
        headers: { ...(await requestHeaders(identity)), Accept: "text/event-stream" },
        body: JSON.stringify(payload),
        signal: abortSignal,
      });
    } catch (error) {
      if (error?.name === "AbortError") return;
      console.error(`[AskAddyV2] network error reaching ${BASE_URL}`, error);
      yield { type: "error", message: "Couldn't reach Addy. Check your connection, then try again.", retryable: true };
      return;
    }
    console.info("[AskAddyV2] ←", path, response.status);
    if (!response.ok || !response.body) {
      yield { type: "error", message: await failure(response) };
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let isFinished = false;
    for (;;) {
      let chunk;
      try {
        chunk = await readWithin(reader, STREAM_STALL_MS);
      } catch (error) {
        if (error?.name === "AbortError") return;
        // Proxies and idle timeouts can drop the connection after the last event; the answer is whole.
        if (isFinished) return;
        reader.cancel?.().catch?.(() => undefined);
        // The run keeps going on the server (it outlives the connection), so the caller fetches the
        // finished answer instead of showing an error: see recoverFinishedAnswer in runtime.js.
        console.warn(`[AskAddyV2] stream from ${path} ${error?.name === "StallError" ? "stalled" : "dropped"} before the answer finished`, error);
        yield { type: "interrupted", reason: error?.name === "StallError" ? "stalled" : "dropped" };
        return;
      }
      if (chunk.done) break;
      buffer += decoder.decode(chunk.value, { stream: true });
      const frames = buffer.split("\n\n");
      buffer = frames.pop() || "";
      for (const frame of frames) {
        const line = frame.split("\n").find((l) => l.startsWith("data: "));
        if (!line) continue;
        let event;
        try {
          event = JSON.parse(line.slice(6));
        } catch (_) {
          continue; // a malformed frame is skipped, not fatal
        }
        if (FINISH_EVENTS.has(event?.type)) isFinished = true;
        yield event;
      }
    }
  },

  /**
   * A new question. `attachments` are upload ids from upload(); `documentIds` limit the question
   * to documents the loan officer picked on the loan; `agentId` (an agent's appID) is the agent
   * Ask Addy answers as.
   */
  query({ query, loanId, scenarioId, threadId, agentId, attachments, documentIds, identity, abortSignal }) {
    return this.events(
      "/ask-addy/v2/query-stream",
      {
        query,
        ...openRecord({ loanId, scenarioId }),
        threadId,
        ...(agentId ? { appID: agentId } : {}),
        ...(attachments?.length ? { attachments } : {}),
        ...(documentIds?.length ? { documentIds } : {}),
      },
      identity,
      abortSignal
    );
  },

  /** The loan officer's decision on a paused action; streams the rest of the answer. */
  decide({ approval, approved, loanId, scenarioId, identity, abortSignal }) {
    return this.events(
      "/ask-addy/v2/approval",
      { runId: approval.runId, toolCallId: approval.toolCallId, threadId: approval.threadId, ...openRecord({ loanId, scenarioId }), approved },
      identity,
      abortSignal
    );
  },

  /** Hand a paused tool its result (e.g. what use_my_browser did); streams the rest of the answer. */
  resume({ paused, resumeData, loanId, scenarioId, identity, abortSignal }) {
    return this.events(
      "/ask-addy/v2/approval",
      { runId: paused.runId, toolCallId: paused.toolCallId, threadId: paused.threadId, ...openRecord({ loanId, scenarioId }), resumeData },
      identity,
      abortSignal
    );
  },

  /** Attach a file (e.g. a lender worksheet) to the next question. Returns { uploadId, fileName, sizeBytes }. */
  async upload(file, identity) {
    if (file.size > SINGLE_UPLOAD_MAX_BYTES) return this.uploadInChunks(file, identity);
    const form = new FormData();
    form.append("file", file);
    const response = await fetch(`${BASE_URL}/ask-addy/v2/uploads`, { method: "POST", headers: await requestHeaders(identity, false), body: form });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.success === false) throw new Error(body.reason || `Upload failed (HTTP ${response.status})`);
    return body.data;
  },

  /** upload() for a file past one request: start it, then send it chunk by chunk, resuming from what the server has. */
  async uploadInChunks(file, identity) {
    const started = await fetch(`${BASE_URL}/ask-addy/v2/uploads/start`, {
      method: "POST",
      headers: await requestHeaders(identity),
      body: JSON.stringify({ fileName: file.name, sizeBytes: file.size }),
    });
    const startBody = await started.json().catch(() => ({}));
    if (!started.ok || startBody.success === false) throw new Error(startBody.reason || `Upload failed (HTTP ${started.status})`);
    const { uploadId, chunkBytes } = startBody.data;
    let offset = 0;
    let retries = 0;
    for (;;) {
      const response = await fetch(`${BASE_URL}/ask-addy/v2/uploads/${encodeURIComponent(uploadId)}?offset=${offset}`, {
        method: "PUT",
        headers: { ...(await requestHeaders(identity, false)), "Content-Type": "application/octet-stream" },
        body: file.slice(offset, offset + chunkBytes),
      }).catch(() => null);
      const body = response ? await response.json().catch(() => ({})) : {};
      if (response?.ok && body.data?.upload) return body.data.upload;
      if (response?.ok) {
        offset = body.data.received;
        retries = 0;
        continue;
      }
      // A dropped connection may still have delivered the chunk: the server's 409 says how much it has.
      const canRetry = !response || response.status === 409 || response.status >= 500;
      if (!canRetry || retries >= CHUNK_RETRIES) throw new Error(body.reason || `Upload failed (HTTP ${response?.status ?? "no response"})`);
      retries += 1;
      if (typeof body.data?.received === "number") offset = body.data.received;
    }
  },

  /** The loan's documents, for picking which ones a question is about: [{ documentId, name, group, url, … }]. */
  async loanDocuments(loanId, identity) {
    const response = await fetch(`${BASE_URL}/ask-addy/v2/loans/${encodeURIComponent(loanId)}/documents`, { headers: await requestHeaders(identity, false) });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.success === false) throw new Error(body.reason || `HTTP ${response.status}`);
    return body.data?.documents || [];
  },

  /**
   * Who the assistant is: the picked agent (`agentId`, an agent's appID) from the instructions its
   * organization wrote for it in AI Agents, or Lendy. { assistantName, instructions, appID? }.
   */
  async assistant(identity, agentId) {
    const query = agentId ? `?appID=${encodeURIComponent(agentId)}` : "";
    const response = await fetch(`${BASE_URL}/ask-addy/v2/assistant${query}`, { headers: await requestHeaders(identity, false) });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.success === false) throw new Error(body.reason || `HTTP ${response.status}`);
    return body.data;
  },

  /** A new chat's suggested questions, written for this loan officer: [] when the server has none. */
  async starters(identity) {
    const response = await fetch(`${BASE_URL}/ask-addy/v2/starters`, { headers: await requestHeaders(identity, false) });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.success === false) throw new Error(body.reason || `HTTP ${response.status}`);
    return body.data?.starters || [];
  },

  /** One line about the loan: { borrowers, address, loanAmount, purpose, stage, documentCount }. */
  async loanSummary(loanId, identity) {
    const response = await fetch(`${BASE_URL}/ask-addy/v2/loans/${encodeURIComponent(loanId)}/summary`, { headers: await requestHeaders(identity, false) });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.success === false) throw new Error(body.reason || `HTTP ${response.status}`);
    return body.data;
  },

  /** The open Scenario Center scenario: { scenarioId, title, status }. */
  async scenarioSummary(scenarioId, identity) {
    const response = await fetch(`${BASE_URL}/ask-addy/v2/scenarios/${encodeURIComponent(scenarioId)}/summary`, { headers: await requestHeaders(identity, false) });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.success === false) throw new Error(body.reason || `HTTP ${response.status}`);
    return body.data;
  },

  /** This user's v2 conversations, newest first (only this loan's or scenario's when one is given): [{ id, title, updatedAt }]. */
  async threads(loanId, identity, scenarioId) {
    const open = Object.entries(openRecord({ loanId, scenarioId }))[0];
    const query = open ? `?${open[0]}=${encodeURIComponent(open[1])}` : "";
    const response = await fetch(`${BASE_URL}/ask-addy/v2/threads${query}`, { headers: await requestHeaders(identity, false) });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.success === false) throw new Error(body.reason || `HTTP ${response.status}`);
    return body.data || [];
  },

  /**
   * One page of this user's v2 conversations, newest first ({ threads, nextPage }; nextPage is null at
   * the end). The first page also has every pinned or filed one. Off a loan or scenario, only the
   * general conversations: theirs need their context.
   */
  async threadsPage(loanId, identity, scenarioId, { page = 0, perPage = 20 } = {}) {
    const open = Object.entries(openRecord({ loanId, scenarioId }))[0];
    const query = new URLSearchParams({
      ...(open ? { [open[0]]: open[1] } : { generalOnly: "true" }),
      page: String(page),
      perPage: String(perPage),
    });
    const response = await fetch(`${BASE_URL}/ask-addy/v2/threads?${query}`, { headers: await requestHeaders(identity, false) });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.success === false) throw new Error(body.reason || `HTTP ${response.status}`);
    return { threads: body.data || [], nextPage: typeof body.nextPage === "number" ? body.nextPage : null };
  },

  /** Deletes one of this user's v2 conversations for good; the server checks it is theirs. */
  async deleteThread(threadId, identity) {
    const response = await fetch(`${BASE_URL}/ask-addy/v2/threads/${encodeURIComponent(threadId)}`, {
      method: "DELETE",
      headers: await requestHeaders(identity, false),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.success === false) throw new Error(body.reason || `HTTP ${response.status}`);
  },

  /** Renames one of this user's v2 conversations; resolves to the title as the list shows it. */
  async renameThread(threadId, title, identity) {
    const response = await fetch(`${BASE_URL}/ask-addy/v2/threads/${encodeURIComponent(threadId)}`, {
      method: "PATCH",
      headers: await requestHeaders(identity),
      body: JSON.stringify({ title }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.success === false) throw new Error(body.reason || `HTTP ${response.status}`);
    return body.data.title;
  },

  /** Pins or files one of this user's v2 conversations (`{ pinned }`, `{ folderId }`; a null folderId takes it out); resolves to its metadata. */
  async updateThread(threadId, changes, identity) {
    const response = await fetch(`${BASE_URL}/ask-addy/v2/threads/${encodeURIComponent(threadId)}`, {
      method: "PATCH",
      headers: await requestHeaders(identity),
      body: JSON.stringify(changes),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.success === false) throw new Error(body.reason || `HTTP ${response.status}`);
    return body.data.metadata;
  },

  /**
   * Autopilot: jobs Addy runs on its own (schedules, heartbeats, loan events) and the inbox their
   * results land in. All calls act as the signed-in loan officer.
   */
  autopilot: {
    async call(path, identity, { method = "GET", body } = {}) {
      const response = await fetch(`${BASE_URL}/ask-addy/v2/autopilot${path}`, {
        method,
        headers: await requestHeaders(identity, Boolean(body)),
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) throw new Error(data.reason || `HTTP ${response.status}`);
      return data.data;
    },
    status: (identity) => AskAddyV2.autopilot.call("/status", identity),
    jobs: (identity) => AskAddyV2.autopilot.call("/jobs", identity),
    createJob: (job, identity) => AskAddyV2.autopilot.call("/jobs", identity, { method: "POST", body: job }),
    updateJob: (id, patch, identity) => AskAddyV2.autopilot.call(`/jobs/${id}`, identity, { method: "POST", body: patch }),
    deleteJob: (id, identity) => AskAddyV2.autopilot.call(`/jobs/${id}`, identity, { method: "DELETE" }),
    runJob: (id, identity) => AskAddyV2.autopilot.call(`/jobs/${id}/run`, identity, { method: "POST", body: {} }),
    inbox: (identity) => AskAddyV2.autopilot.call("/inbox", identity),
    markInbox: (id, status, identity) => AskAddyV2.autopilot.call(`/inbox/${id}`, identity, { method: "POST", body: { status } }),
    decide: (id, approved, identity) => AskAddyV2.autopilot.call(`/inbox/${id}/decide`, identity, { method: "POST", body: { approved } }),
  },

  /** What Addy has learned (durable memory): the LO's own. */
  memories: {
    async call(path, identity, { method = "GET", body } = {}) {
      const response = await fetch(`${BASE_URL}/ask-addy/v2${path}`, {
        method,
        headers: await requestHeaders(identity, Boolean(body)),
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) throw new Error(data.reason || `HTTP ${response.status}`);
      return data.data;
    },
    list: (identity) => AskAddyV2.memories.call("/memories", identity),
    forget: (id, identity) => AskAddyV2.memories.call(`/memories/${id}`, identity, { method: "DELETE" }),
  },

  /** The chats drawer's folders: [{ id, name, createdAt }]. Deleting one moves its chats back to the list. */
  chatFolders: {
    list: (identity) => AskAddyV2.memories.call("/chat-folders", identity),
    create: (name, identity) => AskAddyV2.memories.call("/chat-folders", identity, { method: "POST", body: { name } }),
    rename: (id, name, identity) => AskAddyV2.memories.call(`/chat-folders/${encodeURIComponent(id)}`, identity, { method: "PATCH", body: { name } }),
    remove: (id, identity) => AskAddyV2.memories.call(`/chat-folders/${encodeURIComponent(id)}`, identity, { method: "DELETE" }),
  },

  /** Thumbs up or down on an answer: the reply to the turn-th question of a chat (from 0). */
  feedback: {
    /** { [turn]: { rating: "up" | "down", reason? } } */
    list: (threadId, identity) => AskAddyV2.memories.call(`/threads/${encodeURIComponent(threadId)}/feedback`, identity),
    /** `rating: null` takes it back; a thumbs down carries what didn't work in `reason`. */
    rate: (threadId, { turn, rating, reason, question, answer }, identity) =>
      AskAddyV2.memories.call(`/threads/${encodeURIComponent(threadId)}/feedback`, identity, { method: "POST", body: { turn, rating, reason, question, answer } }),
  },

  /** MEMORY.md: the LO's profile and what Addy learned, as one document. Saving writes both back. */
  memoryDoc: {
    get: (identity) => AskAddyV2.memories.call("/memory-doc", identity),
    save: (markdown, identity) => AskAddyV2.memories.call("/memory-doc", identity, { method: "PUT", body: { markdown } }),
  },

  /**
   * The LO's own connected apps (Composio): Outlook, Calendar, Drive, Slack… Each person connects
   * their own accounts; Addy's chat uses whatever is connected.
   */
  integrations: {
    /**
     * { enabled, apps: [{ app, name, logo, connected }], cursor }; `limit` is the page size (up to 50).
     * With `categories` (Composio's, e.g. ["crm"]): those categories' apps, most used first, and `categories` back.
     * With `sort: "alphabetical"` (and no search): the whole catalog A to Z, a page at a time.
     */
    list: ({ search = "", connected = false, cursor = "", limit = 0, categories = [], sort = "" } = {}, identity) => {
      const q = new URLSearchParams({
        ...(categories.length ? { categories: categories.join(",") } : {}),
        ...(sort ? { sort } : {}),
        ...(search ? { search } : {}),
        ...(connected ? { connected: "true" } : {}),
        ...(cursor ? { cursor } : {}),
        ...(limit ? { limit: String(limit) } : {}),
      });
      return AskAddyV2.memories.call(`/integrations${q.toString() ? `?${q}` : ""}`, identity);
    },
    /** The apps loan officers have connected most, most first: { apps: [{ app, name, logo, connects }] }. */
    popular: (identity) => AskAddyV2.memories.call("/integrations/popular", identity),
    /** A sign-in link for the app, to open in a new tab. */
    connect: (app, identity) => AskAddyV2.memories.call(`/integrations/${encodeURIComponent(app)}/connect`, identity, { method: "POST", body: {} }),
    disconnect: (app, identity) => AskAddyV2.memories.call(`/integrations/${encodeURIComponent(app)}`, identity, { method: "DELETE" }),
  },

  /**
   * The loan officer's own MCP servers (Apps & MCP), which the chat can use:
   * { enabled, servers: [{ id, name, serverUrl, authType, status, lastError, toolCount }] }.
   */
  mcpServers: {
    list: (identity) => AskAddyV2.memories.call("/mcp-servers", identity),
    /** { name, serverUrl, authType: none|apiKey|bearer|oauth, apiKeyHeaderName?, apiKeyValue?, bearerToken?, oauthClientId?, oauthClientSecret?, oauthScope? } */
    add: (server, identity) => AskAddyV2.memories.call("/mcp-servers", identity, { method: "POST", body: server }).then(({ server: added }) => added),
    /** An OAuth server's sign-in link, to open in a new tab. */
    connect: (id, identity) => AskAddyV2.memories.call(`/mcp-servers/${encodeURIComponent(id)}/connect`, identity, { method: "POST", body: {} }),
    scan: (id, identity) => AskAddyV2.memories.call(`/mcp-servers/${encodeURIComponent(id)}/scan`, identity, { method: "POST", body: {} }),
    remove: (id, identity) => AskAddyV2.memories.call(`/mcp-servers/${encodeURIComponent(id)}`, identity, { method: "DELETE" }),
  },

  /** What waits for the loan officer's OK (the Approvals tab): [{ id, askFirst, locked }]. */
  approvals: {
    list: (identity) => AskAddyV2.memories.call("/approvals", identity).then(({ settings }) => settings),
    set: (setting, askFirst, identity) =>
      AskAddyV2.memories.call("/approvals", identity, { method: "POST", body: { setting, askFirst } }).then(({ settings }) => settings),
  },

  /**
   * Is Addy still working in this conversation? A run keeps going when the sidebar closes, so a
   * reopened conversation may have one in progress. Returns { running, question, startedAt }.
   */
  async activeRun(threadId, identity) {
    const response = await fetch(`${BASE_URL}/ask-addy/v2/threads/${encodeURIComponent(threadId)}/active-run`, {
      headers: await requestHeaders(identity, false),
    });
    if (response.status === 404) return { running: false };
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.success === false) throw new Error(body.reason || `HTTP ${response.status}`);
    return body.data || { running: false };
  },

  /** The Stop button. Closing the sidebar does not stop a run; this does. */
  async cancelRun(threadId, identity) {
    await fetch(`${BASE_URL}/ask-addy/v2/runs/cancel`, {
      method: "POST",
      headers: await requestHeaders(identity),
      body: JSON.stringify({ threadId }),
      keepalive: true,
    }).catch(() => undefined);
  },

  /** Earlier messages in a thread: [{ id, role, text, tools:[{toolName,state}], files:[...] }]. */
  async history(threadId, identity) {
    const response = await fetch(`${BASE_URL}/ask-addy/v2/threads/${encodeURIComponent(threadId)}/messages`, {
      headers: await requestHeaders(identity, false),
    });
    if (response.status === 404) return []; // no conversation yet
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.success === false) throw new Error(body.reason || `HTTP ${response.status}`);
    return body.data || [];
  },
};

export default AskAddyV2;
